import {
    SolTransactionReceipt,
    TransactionReceipt,
    type Address,
    type EVMChainConfig,
    type SolChainConfig,
    type SolTransactionRequest,
    type TransactionRequest,
} from "@shared/types";

import {
    Message,
    MessageResponse,
    MessageType,
    OrderNotification,
    type NotificationsCallback,
    type SignAuthMessage,
    type SignMessage,
    type SignTypedData,
    type StatusCallback,
} from "../types";
import type { FunderRegistry, TaggedFunderSender } from "./validateAndPrepare";
import { _openProviderWidget, _waitForPaymentStatus } from "../utils";

import { getState, setState, stripFunctions, type WidgetContext } from "./state";

/**
 * The registry's two `sendTransaction` shapes, flattened to one callable signature.
 *
 * Routing is by ADDRESS: a funder is only ever reached through the key it registered under, so the
 * transaction it receives is always the one its own VM built. TypeScript cannot see that — calling
 * the union of the two signatures would intersect their parameters — so the dispatch casts to this
 * type. The address lookup is the guarantee; this is just how it's expressed to the compiler.
 */
type FunderSend = (
    transaction: TransactionRequest | SolTransactionRequest,
    chainConfig: EVMChainConfig | SolChainConfig,
) => Promise<TransactionReceipt | SolTransactionReceipt>;

/**
 * The registry's custom (host-managed) arm: the only one carrying `getBalances`/`sendFunds`, and the
 * only one with no VM at all. `"CUSTOM"` is a registry-internal discriminant, never a `WalletVm`.
 */
type CustomFunderSender = Extract<TaggedFunderSender, { walletType: "CUSTOM" }>;

// ============================================
// Message Handler Configuration
// ============================================

export type MessageHandlerConfig = {
    getContentWindow: () => Window | null;
    windowOrigin: string;
    providerWindowRef: { current: Window | null };
    hasOwner: boolean;
    hasTxHandler: boolean;
    signMessage?: SignMessage | undefined;
    signTypedData?: SignTypedData | undefined;
    signAuthMessage?: SignAuthMessage | undefined;
    funderRegistry?: FunderRegistry;
    connect?: (() => void) | undefined;
    generateFeeSponsorshipGrant?: ((payload: string) => Promise<string>) | undefined;
    statusCallback?: StatusCallback | undefined;
    notificationsCallback?: NotificationsCallback | undefined;
    apiKey: string;
    apiBaseUrl: string;
    onReady?: (() => void) | undefined;
    onError?: ((error: Error) => void) | undefined;
    onClose?: (() => void) | undefined;
};

// ============================================
// Handler Registration
// ============================================

const cleanMessageHandlers = (ctx: WidgetContext): void => {
    const state = getState(ctx);
    state.messageHandlers.forEach((handler) => {
        window.removeEventListener("message", handler);
    });
    setState({ messageHandlers: [] }, ctx);
};

const registerMessageHandlers = (newHandlers: ((event: MessageEvent) => void)[], ctx: WidgetContext): void => {
    newHandlers.forEach((handler) => {
        window.addEventListener("message", handler);
    });
    setState({ messageHandlers: newHandlers }, ctx);
};

// ============================================
// Setup (clean -> create -> register)
// ============================================

export const setupMessageHandlers = (config: MessageHandlerConfig, ctx: WidgetContext): void => {
    const {
        getContentWindow,
        windowOrigin,
        providerWindowRef,
        hasOwner,
        hasTxHandler,
        signMessage,
        signTypedData,
        signAuthMessage,
        funderRegistry,
        connect,
        generateFeeSponsorshipGrant,
        statusCallback,
        notificationsCallback,
        apiKey,
        apiBaseUrl,
        onReady,
    } = config;

    const hasCustomFunder = !!funderRegistry && [...funderRegistry.values()].some((f) => f.walletType === "CUSTOM");

    // 1. Always clean previous handlers first (this instance's only)
    cleanMessageHandlers(ctx);

    const handlers: ((event: MessageEvent) => void)[] = [];

    // Source check: message came from the widget's content window (iframe or popup).
    // Resolved dynamically so handlers see the current window reference at event time.
    const isFromWidget = (event: MessageEvent): boolean => {
        const cw = getContentWindow();
        return cw !== null && event.source === cw;
    };

    // WIDGET_READY / HALLIDAY_READY handler
    if (onReady) {
        handlers.push((event: MessageEvent) => {
            if (event.origin !== windowOrigin) return;
            if (!isFromWidget(event)) return;
            const { type } = event.data || {};
            if (type === "WIDGET_READY" || type === "HALLIDAY_READY") {
                onReady();
            }
        });
    }

    // EVENT_WINDOW_CLOSE handler (always) — delegates to onClose callback, no handler cleanup
    handlers.push((event: MessageEvent) => {
        if (event.origin !== windowOrigin || !isFromWidget(event)) return;
        const { type } = event.data || {};
        if (type === MessageType.EVENT_WINDOW_CLOSE) {
            config.onClose?.();
        }
    });

    // Signature handlers
    if (hasOwner) {
        handlers.push(async (event: MessageEvent<Message>) => {
            if (event.origin !== windowOrigin || !isFromWidget(event)) return;
            const { type, payload } = event.data || {};
            const widgetWindow = getContentWindow();

            if (type === MessageType.ACTION_SIGN_MESSAGE) {
                const { messageId, message, ownerAddress: msgOwnerAddress } = payload;
                try {
                    const signature =
                        typeof signMessage === "function"
                            ? await signMessage({
                                  message,
                                  ...(msgOwnerAddress !== undefined && { ownerAddress: msgOwnerAddress }),
                              })
                            : (() => {
                                  throw new Error("No signMessage function provided");
                              })();
                    widgetWindow?.postMessage(
                        { type, payload: { messageId, signature } } as MessageResponse,
                        windowOrigin,
                    );
                } catch (error) {
                    console.error("Error signing message", error);
                    widgetWindow?.postMessage(
                        {
                            type,
                            payload: {
                                messageId,
                                error: error instanceof Error ? error.message : error,
                            },
                        } as MessageResponse,
                        windowOrigin,
                    );
                }
            } else if (type === MessageType.ACTION_SIGN_TYPED_DATA) {
                const { messageId, typedData, ownerAddress: tdOwnerAddress, chainId } = payload;
                try {
                    const signature =
                        typeof signTypedData === "function"
                            ? await signTypedData({
                                  typedData,
                                  ...(tdOwnerAddress !== undefined && { ownerAddress: tdOwnerAddress }),
                                  ...(chainId !== undefined && { chainId }),
                              })
                            : (() => {
                                  throw new Error("No signTypedData function provided");
                              })();
                    widgetWindow?.postMessage(
                        { type, payload: { messageId, signature } } as MessageResponse,
                        windowOrigin,
                    );
                } catch (error) {
                    console.error("Error signing typed data", error);
                    widgetWindow?.postMessage(
                        {
                            type,
                            payload: {
                                messageId,
                                error: error instanceof Error ? error.message : error,
                            },
                        } as MessageResponse,
                        windowOrigin,
                    );
                }
            }
        });
    }

    // Auth-message signature handler (wallet-auth owners). Gated purely on the signer's presence and
    // independent of `hasOwner` (wallet-auth owners have hasOwner=false). Signs the widget's sign-in
    // message out-of-band and posts the signature (or a structured error) back — a direct analog of the
    // ACTION_SIGN_MESSAGE handler above.
    if (signAuthMessage) {
        handlers.push(async (event: MessageEvent<Message>) => {
            if (event.origin !== windowOrigin || !isFromWidget(event)) return;
            const { type, payload } = event.data || {};
            const widgetWindow = getContentWindow();

            if (type === MessageType.ACTION_SIGN_AUTH_MESSAGE) {
                const { messageId, message, address, walletType } = payload;
                try {
                    const signature = await signAuthMessage({ message, address, walletType });
                    widgetWindow?.postMessage(
                        { type, payload: { messageId, signature } } as MessageResponse,
                        windowOrigin,
                    );
                } catch (error) {
                    console.error("Error signing auth message", error);
                    widgetWindow?.postMessage(
                        {
                            type,
                            payload: {
                                messageId,
                                error: error instanceof Error ? error.message : error,
                            },
                        } as MessageResponse,
                        windowOrigin,
                    );
                }
            }
        });
    }

    // Fee-sponsorship grant responder. Gated purely on the callback's presence and independent of `hasOwner`
    // (fee sponsorship is orthogonal to owner wallets — wallet-auth/otp-auth owners have hasOwner=false).
    // The host callback is a PURE SIGNER: it receives the widget's canonical `grantPayload` and returns only
    // the signature. We reassemble the `{ grantPayload, grantSignature }` grant and post it (or a structured
    // error) back to the widget, a direct analog of the ACTION_SIGN_AUTH_MESSAGE handler above.
    if (generateFeeSponsorshipGrant) {
        handlers.push(async (event: MessageEvent<Message>) => {
            if (event.origin !== windowOrigin || !isFromWidget(event)) return;
            const { type, payload } = event.data || {};
            const widgetWindow = getContentWindow();

            if (type === MessageType.ACTION_GENERATE_FEE_SPONSORSHIP_GRANT) {
                const { messageId, grantPayload } = payload;
                try {
                    const grantSignature = await generateFeeSponsorshipGrant(grantPayload);
                    widgetWindow?.postMessage(
                        {
                            type,
                            payload: { messageId, grant: { grantPayload, grantSignature }, error: null },
                        } as MessageResponse,
                        windowOrigin,
                    );
                } catch (error) {
                    widgetWindow?.postMessage(
                        {
                            type,
                            payload: {
                                messageId,
                                grant: null,
                                error: error instanceof Error ? error.message : String(error),
                            },
                        } as MessageResponse,
                        windowOrigin,
                    );
                }
            }
        });
    }

    // Transaction handler — routes to correct funder via transaction.from
    if (hasTxHandler && funderRegistry && funderRegistry.size > 0) {
        handlers.push(async (event: MessageEvent<Message>) => {
            if (event.origin !== windowOrigin || !isFromWidget(event)) return;
            const { type, payload } = event.data || {};
            const widgetWindow = getContentWindow();

            if (type === MessageType.ACTION_TRANSACTION) {
                const { messageId, chainConfig, transaction } = payload;
                try {
                    // Route by transaction.from; fallback to first entry only when from is absent
                    const fromKey = transaction.from ? String(transaction.from).toLowerCase() : undefined;
                    // Annotated: `Map.values().next().value` is `any`, which would otherwise collapse
                    // this binding and silently disable type checking for the rest of the handler.
                    const funder: TaggedFunderSender | undefined = fromKey
                        ? funderRegistry.get(fromKey)
                        : funderRegistry.values().next().value;
                    if (!funder) {
                        throw new Error(
                            fromKey
                                ? `No funder registered for address ${transaction.from}`
                                : "No sendTransaction function provided",
                        );
                    }
                    if (funder.walletType === "CUSTOM") {
                        throw new Error(
                            `Funder for ${transaction.from ?? "(default)"} is a custom (host-managed) funder and cannot send transactions`,
                        );
                    }
                    if (chainConfig.address_family !== funder.walletType) {
                        throw new Error(
                            `Funder for ${transaction.from ?? "(default)"} is ${funder.walletType}, but this transaction is ${chainConfig.address_family}`,
                        );
                    }
                    const txReceipt: TransactionReceipt | SolTransactionReceipt = await (
                        funder.sendTransaction as FunderSend
                    )(transaction, chainConfig);
                    widgetWindow?.postMessage(
                        { type, payload: { messageId, txReceipt } } as MessageResponse,
                        windowOrigin,
                    );
                } catch (error) {
                    console.error("Error handling transaction", error);
                    widgetWindow?.postMessage(
                        {
                            type,
                            payload: {
                                messageId,
                                error: error instanceof Error ? error.message : error,
                            },
                        } as MessageResponse,
                        windowOrigin,
                    );
                }
            }
        });
    }

    if (funderRegistry && hasCustomFunder) {
        const resolveCustomFunder = (funderAddress: Address): CustomFunderSender => {
            const funder = funderRegistry.get(String(funderAddress).toLowerCase());
            if (!funder || funder.walletType !== "CUSTOM") {
                throw new Error(`No custom funder registered for address ${funderAddress}`);
            }
            return funder;
        };

        handlers.push(async (event: MessageEvent<Message>) => {
            if (event.origin !== windowOrigin || !isFromWidget(event)) return;
            const { type, payload } = event.data || {};
            const widgetWindow = getContentWindow();

            if (type === MessageType.ACTION_GET_BALANCES) {
                const { messageId, funderAddress } = payload;
                try {
                    const balances = await resolveCustomFunder(funderAddress).getBalances();
                    widgetWindow?.postMessage(
                        { type, payload: { messageId, balances, error: null } } as MessageResponse,
                        windowOrigin,
                    );
                } catch (error) {
                    widgetWindow?.postMessage(
                        {
                            type,
                            payload: {
                                messageId,
                                balances: null,
                                error: error instanceof Error ? error.message : String(error),
                            },
                        } as MessageResponse,
                        windowOrigin,
                    );
                }
            } else if (type === MessageType.ACTION_TRANSFER_FUNDS) {
                const { messageId, funderAddress, instruction } = payload;
                try {
                    await resolveCustomFunder(funderAddress).sendFunds(instruction);
                    widgetWindow?.postMessage(
                        { type, payload: { messageId, ok: true, error: null } } as MessageResponse,
                        windowOrigin,
                    );
                } catch (error) {
                    widgetWindow?.postMessage(
                        {
                            type,
                            payload: {
                                messageId,
                                ok: false,
                                error: error instanceof Error ? error.message : String(error),
                            },
                        } as MessageResponse,
                        windowOrigin,
                    );
                }
            } else if (type === MessageType.ACTION_GET_FEE) {
                const { messageId, funderAddress, assetIdentifier, amount } = payload;
                try {
                    const funder = resolveCustomFunder(funderAddress);
                    if (typeof funder.getFee !== "function") {
                        throw new Error(`Funder ${funderAddress} has no getFee`);
                    }
                    const fee = String(await funder.getFee(assetIdentifier, amount));
                    widgetWindow?.postMessage(
                        { type, payload: { messageId, fee, error: null } } as MessageResponse,
                        windowOrigin,
                    );
                } catch (error) {
                    widgetWindow?.postMessage(
                        {
                            type,
                            payload: {
                                messageId,
                                fee: null,
                                error: error instanceof Error ? error.message : String(error),
                            },
                        } as MessageResponse,
                        windowOrigin,
                    );
                }
            }
        });
    }

    // Trigger host wallet connection
    if (connect) {
        handlers.push((event: MessageEvent<Message>) => {
            if (event.origin !== windowOrigin || !isFromWidget(event)) return;
            const { type } = event.data || {};
            if (type === MessageType.ACTION_TRIGGER_CONNECT) {
                connect();
            }
        });
    }

    // Status callback handler
    if (statusCallback) {
        handlers.push((event: MessageEvent<Message>) => {
            if (event.origin !== windowOrigin || !isFromWidget(event)) return;
            const { type, payload } = event.data || {};
            if (type === MessageType.EVENT_ORDER_STATUS) {
                statusCallback({ type, payload });
            }
        });
    }

    // Notifications callback handler — full-set semantics. Per-item tolerant: an unknown or
    // malformed entry is dropped alone, so a future item shape can never wipe the whole set.
    if (notificationsCallback) {
        handlers.push((event: MessageEvent<Message>) => {
            if (event.origin !== windowOrigin || !isFromWidget(event)) return;
            const { type, payload } = event.data || {};
            if (type === MessageType.EVENT_ORDER_NOTIFICATIONS && Array.isArray(payload)) {
                const items = payload
                    .map((item) => OrderNotification.safeParse(item))
                    .flatMap((result) => (result.success ? [result.data] : []));
                // A non-empty set that parses to nothing is garbage, not a clear.
                if (payload.length === 0 || items.length > 0) notificationsCallback(items);
            }
        });
    }


    // Provider widget handler (always)
    handlers.push(async (event: MessageEvent<Message>) => {
        if (event.origin !== windowOrigin || !isFromWidget(event)) return;
        const { type, payload } = event.data || {};
        const widgetWindow = getContentWindow();

        if (type === MessageType.ACTION_PROVIDER_WIDGET && widgetWindow) {
            const { url, redirectUrl, providerWidgetMode, workflowId } = payload || {};
            if (!url || !redirectUrl || !providerWidgetMode || !workflowId) return;

            let tempWindow: Window | null = null;
            if (providerWidgetMode === "REDIRECT") {
                (widgetWindow as Window & { location: string }).location = url;
                tempWindow = widgetWindow;
            } else if (providerWidgetMode === "POPUP") {
                providerWindowRef.current = _openProviderWidget(url);
                tempWindow = providerWindowRef.current;
            }

            if (tempWindow) {
                await _waitForPaymentStatus({
                    apiKey,
                    baseURL: apiBaseUrl,
                    workflowId,
                    window: tempWindow,
                });

                if (!tempWindow.closed) {
                    if (providerWidgetMode === "REDIRECT") {
                        (widgetWindow as Window & { location: string }).location = redirectUrl;
                    } else if (providerWidgetMode === "POPUP") {
                        providerWindowRef.current?.close();
                        providerWindowRef.current = null;
                    }
                }
            }
        }
    });

    // 3. Register all new handlers
    registerMessageHandlers(handlers, ctx);
};

// ============================================
// PostMessage Senders
// ============================================

export const sendUpdateParams = (
    contentWindow: Window | null,
    mergedConfig: Record<string, unknown>,
    windowOrigin: string,
): void => {
    const stripped = stripFunctions(mergedConfig);
    contentWindow?.postMessage({ type: "HALLIDAY_UPDATE_PARAMS", payload: stripped }, windowOrigin);
};

export const sendShow = (iframe: HTMLIFrameElement | null, windowOrigin: string): void => {
    setTimeout(() => {
        const target = iframe?.contentWindow;
        target?.postMessage({ type: "HALLIDAY_SHOW" }, windowOrigin);
    }, 10);
};

// Re-export cleanMessageHandlers for destroyClient
export { cleanMessageHandlers };
